import React, { useCallback } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import {
  FDPrimaryActionButton,
  FDContentContainer,
  FDPaper,
  FDStack,
  FDImage,
  FDBox, FDEmphasisSurface,
} from '@facet-wealth/design';
import { HeadlineTextMedium, HeadlineTextLarge } from 'Packages/FacetDesign/Components';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import FileUploader from 'Components/Files/Uploader2';
import { navigateToPage } from 'Views/Page/Content/Templates';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { CompleteActionItemBanner } from 'Components/ComponentLibrary/Organisms/CompleteActionItemBanner';
import { dataAttrs } from 'Helpers/Testing';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('name', { _component: () => HeadlineTextMedium }, overrideReadOnly)
  .nameOverride('title', { _component: () => HeadlineTextLarge }, overrideReadOnly);

export const NMARSEmptyPageTemplate = ({ resource }) => {
  const { refreshPageContentCollection } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();
  const [uploading, setUploading] = React.useState(false);
  const isMobile = useMobileLayout();
  useBoneBackgroundColor();


  const onActionClick = useCallback((rel) => {
    const url = rel.link('related').url();
    if (rel.meta('modal') === 'true') {
      return loadModalCollectionByLink(url);
    }
    return navigateToPage(url, false);
  }, [loadModalCollectionByLink]);

  return (
    <>
      <CompleteActionItemBanner resource={resource} btnText="Skip" />
      <FDContentContainer maxWidth="lg" {...dataAttrs('empty-state-page', resource)}>
        <FDStack direction="column" spacing={4} paddingY={3}>
          <MetaControl resource={resource} attrName={'name'} metaOverrideSet={metaOverrideSet} />
          <MetaControl resource={resource} attrName="subHeader" />
          <FDPaper appearance="elevated">
            <FDStack direction="row">
              <FDBox padding={3}>
                <FDStack direction="column" spacing={3} flexGrow={1} height="100%" justifyContent="center">
                  <MetaControl resource={resource} attrName={'title'} metaOverrideSet={metaOverrideSet} />
                  <MetaControl resource={resource} attrName={'description'} metaOverrideSet={metaOverrideSet} />

                  <FDStack justifyContent={'center'} alignItems={'center'} spacing={1}>
                    {resource.withRelationship(
                      fr.relationships.getStarted.name,
                      (rel) => (
                        <FileUploader
                          successToastMessage="File successfully uploaded"
                          url={rel.link('related').url()}
                          onStart={() => {
                            setUploading(true);
                          }}
                          onSuccess={() => refreshPageContentCollection()}
                          onFinally={() => {
                            setUploading(false);
                          }}
                          params={null}
                        >
                          <FDPrimaryActionButton loading={uploading} label={rel.meta('prompt')} icon={'upload'} />
                        </FileUploader>
                      ),
                      () => null,
                    )}
                    {resource.withRelationship(
                      fr.relationships.homePage.name,
                      (rel) => (
                        <FDPrimaryActionButton
                          label={rel.meta('prompt')}
                          onClick={() => {
                            onActionClick(rel);
                          }}
                        />
                      ),
                      () => null,
                    )}
                  </FDStack>
                </FDStack>
              </FDBox>
              {isMobile ? null : resource.withRelationship(fr.relationships.image.name,
                (rel) => (
                  <FDStack maxWidth="500px" flexShrink={0}>
                    <FDEmphasisSurface padding={8}>
                      <FDStack alignItems="center" height="100%">
                        <FDImage src={rel.link('related').url()} alt="hero image" aspectRatio="382/282" width="100%" height="400px" />
                      </FDStack>
                    </FDEmphasisSurface>
                  </FDStack>
                ),
                () => {})}
            </FDStack>
          </FDPaper>
        </FDStack>
      </FDContentContainer></>
  );
};

NMARSEmptyPageTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
};
